<template>
  <div class="text-background-fg/70 relative min-w-64 rounded-md shadow">
    <HeadlessListbox
      :model-value="intervalType"
      @update:model-value="onUpdateIntervalType"
    >
      <HeadlessListboxButton
        ref="listboxButtonRef"
        class="focus:border-primary focus:ring-primary relative flex w-full cursor-pointer items-center justify-center rounded-md border border-black/20 bg-white px-10 py-2 text-center font-semibold shadow-sm focus:outline-none sm:text-sm"
      >
        <button
          class="absolute bottom-0 left-0 top-0 flex items-center justify-center p-1"
          type="button"
          :disabled="intervalType === 'custom'"
          @click.prevent="onIntervalPreviousClick"
        >
          <IconHeroiconsSolidChevronLeft class="h-6 w-6" />
        </button>
        <span class="block truncate">
          {{ formattedModelValue }}
        </span>
        <button
          class="absolute bottom-0 right-0 top-0 flex items-center justify-center p-1"
          type="button"
          :disabled="
            intervalType === 'custom' || (props.disableFutureDates && isAtToday)
          "
          @click.prevent="onIntervalNextClick"
        >
          <IconHeroiconsSolidChevronRight class="h-6 w-6" />
        </button>
      </HeadlessListboxButton>
      <Teleport to="body">
        <Transition
          leave-active-class="transition ease-in duration-100"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <HeadlessListboxOptions
            ref="listboxOptionsRef"
            as="template"
            class="bg-background ring-background-fg absolute z-10 overflow-auto rounded-md text-base shadow-lg ring-1 ring-opacity-5 focus:outline-none sm:text-sm"
          >
            <div class="flex">
              <ul>
                <template
                  v-for="option in intervalTypeOptions"
                  :key="option.value"
                >
                  <HeadlessListboxOption
                    v-slot="{ active, selected }"
                    as="template"
                    :value="option.value"
                  >
                    <li
                      :class="[
                        active
                          ? 'bg-primary text-primary-fg'
                          : 'text-background-fg/87',
                        'relative cursor-pointer select-none py-2 pl-3 pr-9',
                      ]"
                    >
                      <span
                        :class="[
                          selected ? 'font-semibold' : 'font-normal',
                          'block truncate',
                        ]"
                      >
                        {{ option.label }}
                      </span>
                    </li>
                  </HeadlessListboxOption>
                </template>
              </ul>
              <div class="p-1">
                <DateSelect
                  v-model="datePickerModel"
                  :allow-range="false"
                  type="day"
                  :disable-future-dates="props.disableFutureDates"
                  class="w-64"
                />
              </div>
            </div>
          </HeadlessListboxOptions>
        </Transition>
      </Teleport>
    </HeadlessListbox>
  </div>
</template>

<script setup lang="ts">
import { createPopper } from "@popperjs/core";
import { type ComponentPublicInstance } from "vue";

type FiniteDateInterval = {
  start: Date;
  end: Date;
};

type DateInterval = Partial<FiniteDateInterval>;

type IntervalType = "day" | "week" | "month" | "year" | "allTime" | "custom";

const props = defineProps<{
  modelValue: DateInterval;
  disableUnbounded?: boolean;
  disableFutureDates?: boolean;
}>();

const emit = defineEmits<{
  (event: "update:modelValue", value: DateInterval): void;
}>();

const datePickerModel = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const intervalIsFinite = (
  dateInterval: DateInterval,
): dateInterval is FiniteDateInterval =>
  Boolean(dateInterval.start && dateInterval.end);

const intervalIsDay = (dateInterval: DateInterval) =>
  intervalIsFinite(dateInterval) &&
  DateFns.isEqual(DateFns.startOfDay(dateInterval.start), dateInterval.start) &&
  DateFns.isEqual(DateFns.endOfDay(dateInterval.start), dateInterval.end);

const intervalIsWeek = (dateInterval: DateInterval) =>
  intervalIsFinite(dateInterval) &&
  DateFns.isEqual(
    DateFns.startOfISOWeek(dateInterval.start),
    dateInterval.start,
  ) &&
  DateFns.isEqual(DateFns.endOfISOWeek(dateInterval.start), dateInterval.end);

const intervalIsMonth = (dateInterval: DateInterval) =>
  intervalIsFinite(dateInterval) &&
  DateFns.isEqual(
    DateFns.startOfMonth(dateInterval.start),
    dateInterval.start,
  ) &&
  DateFns.isEqual(DateFns.endOfMonth(dateInterval.start), dateInterval.end);

const intervalIsYear = (dateInterval: DateInterval) =>
  intervalIsFinite(dateInterval) &&
  DateFns.isEqual(
    DateFns.startOfYear(dateInterval.start),
    dateInterval.start,
  ) &&
  DateFns.isEqual(DateFns.endOfYear(dateInterval.end), dateInterval.end);

const intervalIsAllTime = (dateInterval: DateInterval) =>
  dateInterval.start === undefined && dateInterval.end === undefined;

const intervalType = computed<IntervalType>(() => {
  if (intervalIsDay(props.modelValue)) {
    return "day";
  } else if (intervalIsWeek(props.modelValue)) {
    return "week";
  } else if (intervalIsMonth(props.modelValue)) {
    return "month";
  } else if (intervalIsAllTime(props.modelValue)) {
    return "allTime";
  } else if (intervalIsYear(props.modelValue)) {
    return "year";
  } else {
    return "custom";
  }
});

const intervalTypeOptions = computed<
  Array<{
    value: IntervalType;
    label: string;
  }>
>(() => [
  {
    value: "day",
    label: "Today",
  },
  {
    value: "week",
    label: "This Week",
  },
  {
    value: "month",
    label: "This Month",
  },
  {
    value: "year",
    label: "This Year",
  },
  ...(props.disableUnbounded
    ? []
    : ([
        {
          value: "allTime",
          label: "All Time",
        },
      ] as const)),
]);

const onIntervalPreviousClick = () => {
  let newModelValue: DateInterval;

  if (!props.modelValue.start || !props.modelValue.end) {
    return;
  }
  switch (intervalType.value) {
    case "day": {
      const start = DateFns.startOfDay(
        DateFns.addDays(props.modelValue.start, -1),
      );
      newModelValue = {
        start,
        end: DateFns.endOfDay(start),
      };
      break;
    }
    case "week": {
      const start = DateFns.addWeeks(
        DateFns.startOfISOWeek(props.modelValue.start),
        -1,
      );
      newModelValue = {
        start,
        end: DateFns.endOfISOWeek(start),
      };
      break;
    }
    case "month": {
      const start = DateFns.addMonths(
        DateFns.startOfMonth(props.modelValue.start),
        -1,
      );

      newModelValue = {
        start,
        end: DateFns.endOfMonth(start),
      };
      break;
    }
    case "year": {
      const start = DateFns.addYears(
        DateFns.startOfYear(props.modelValue.start),
        -1,
      );

      newModelValue = {
        start,
        end: DateFns.endOfYear(start),
      };
      break;
    }
    default:
      newModelValue = props.modelValue;
  }
  emit("update:modelValue", newModelValue);
};

const onIntervalNextClick = () => {
  let newModelValue: DateInterval;
  if (!props.modelValue.start || !props.modelValue.end) {
    return;
  }
  switch (intervalType.value) {
    case "day": {
      const start = DateFns.startOfDay(
        DateFns.addDays(props.modelValue.start, 1),
      );
      if (
        props.disableFutureDates &&
        DateFns.isAfter(start, DateFns.endOfDay(new Date()))
      ) {
        return;
      }
      newModelValue = {
        start,
        end: DateFns.endOfDay(start),
      };
      break;
    }
    case "week": {
      const start = DateFns.addWeeks(
        DateFns.startOfISOWeek(props.modelValue.start),
        1,
      );
      if (
        props.disableFutureDates &&
        DateFns.isAfter(start, DateFns.endOfDay(new Date()))
      ) {
        return;
      }
      const weekEnd = props.disableFutureDates
        ? DateFns.min([
            DateFns.endOfISOWeek(start),
            DateFns.endOfDay(new Date()),
          ])
        : DateFns.endOfISOWeek(start);
      newModelValue = {
        start: start,
        end: weekEnd,
      };
      break;
    }
    case "month": {
      const start = DateFns.addMonths(
        DateFns.startOfMonth(props.modelValue.start),
        1,
      );
      if (
        props.disableFutureDates &&
        DateFns.isAfter(start, DateFns.endOfDay(new Date()))
      ) {
        return;
      }
      const monthEnd = props.disableFutureDates
        ? DateFns.min([DateFns.endOfMonth(start), DateFns.endOfDay(new Date())])
        : DateFns.endOfMonth(start);
      newModelValue = {
        start: start,
        end: monthEnd,
      };
      break;
    }
    case "year": {
      const start = DateFns.addYears(
        DateFns.startOfYear(props.modelValue.start),
        1,
      );
      if (
        props.disableFutureDates &&
        DateFns.isAfter(start, DateFns.endOfDay(new Date()))
      ) {
        return;
      }
      const yearEnd = props.disableFutureDates
        ? DateFns.min([DateFns.endOfYear(start), DateFns.endOfDay(new Date())])
        : DateFns.endOfYear(start);
      newModelValue = {
        start: start,
        end: yearEnd,
      };
      break;
    }
    default:
      newModelValue = props.modelValue;
  }

  emit("update:modelValue", newModelValue);
};

const onUpdateIntervalType = (newType: IntervalType) => {
  const today = new Date();

  let newModelValue: DateInterval;
  switch (newType) {
    case "day": {
      newModelValue = {
        start: DateFns.startOfDay(today),
        end: DateFns.endOfDay(today),
      };
      break;
    }
    case "week": {
      const weekStart = DateFns.startOfISOWeek(today);
      const weekEnd = props.disableFutureDates
        ? DateFns.min([DateFns.endOfISOWeek(today), DateFns.endOfDay(today)])
        : DateFns.endOfISOWeek(today);
      newModelValue = {
        start: weekStart,
        end: weekEnd,
      };
      break;
    }
    case "month": {
      const monthStart = DateFns.startOfMonth(today);
      const monthEnd = props.disableFutureDates
        ? DateFns.min([DateFns.endOfMonth(today), DateFns.endOfDay(today)])
        : DateFns.endOfMonth(today);
      newModelValue = {
        start: monthStart,
        end: monthEnd,
      };
      break;
    }
    case "year": {
      const yearStart = DateFns.startOfYear(today);
      const yearEnd = props.disableFutureDates
        ? DateFns.min([DateFns.endOfYear(today), DateFns.endOfDay(today)])
        : DateFns.endOfYear(today);
      newModelValue = {
        start: yearStart,
        end: yearEnd,
      };
      break;
    }
    case "allTime": {
      newModelValue = {
        start: undefined,
        end: undefined,
      };
      break;
    }

    default:
      newModelValue = props.modelValue;
  }

  emit("update:modelValue", newModelValue);
};

const isToday = (interval: DateInterval) =>
  intervalIsFinite(interval) &&
  intervalIsDay(interval) &&
  DateFns.isToday(interval.start);

const isThisWeek = (interval: DateInterval) =>
  intervalIsFinite(interval) &&
  intervalIsWeek(interval) &&
  DateFns.isWithinInterval(new Date(), interval);

const isAtToday = computed(() => {
  if (!props.modelValue.start || !props.modelValue.end) {
    return false;
  }

  const today = new Date();
  const todayStart = DateFns.startOfDay(today);
  const todayEnd = DateFns.endOfDay(today);

  switch (intervalType.value) {
    case "day":
      return (
        DateFns.isEqual(props.modelValue.start, todayStart) &&
        DateFns.isEqual(props.modelValue.end, todayEnd)
      );
    case "week":
      return (
        DateFns.isAfter(props.modelValue.end, todayEnd) ||
        DateFns.isEqual(props.modelValue.end, todayEnd)
      );
    case "month":
      return (
        DateFns.isAfter(props.modelValue.end, todayEnd) ||
        DateFns.isEqual(props.modelValue.end, todayEnd)
      );
    case "year":
      return (
        DateFns.isAfter(props.modelValue.end, todayEnd) ||
        DateFns.isEqual(props.modelValue.end, todayEnd)
      );
    default:
      return false;
  }
});

const formattedModelValue = computed(() => {
  if (intervalIsFinite(props.modelValue)) {
    switch (intervalType.value) {
      case "day": {
        return isToday(props.modelValue)
          ? "Today"
          : DateFns.format(props.modelValue.start, "ccc do MMM");
      }
      case "week": {
        return isThisWeek(props.modelValue)
          ? "This Week"
          : `${DateFns.format(
              props.modelValue.start,
              "do MMM",
            )} - ${DateFns.format(props.modelValue.end, "do MMM")}`;
      }
      case "month": {
        return `${DateFns.format(props.modelValue.start, "MMM yy")}`;
      }
      case "year": {
        return `${DateFns.format(props.modelValue.start, "yyyy")}`;
      }

      default:
        return `${DateFns.format(
          props.modelValue.start,
          "do MMM",
        )} - ${DateFns.format(props.modelValue.end, "do MMM")}`;
    }
  } else if (intervalIsAllTime(props.modelValue)) {
    return "All Time";
  } else {
    return "";
  }
});

const listboxOptionsRef = ref<ComponentPublicInstance | null>(null);
const listboxButtonRef = ref<ComponentPublicInstance | null>(null);

whenever(
  () => listboxButtonRef.value?.$el && listboxOptionsRef.value?.$el,
  () => {
    createPopper(listboxButtonRef.value?.$el, listboxOptionsRef.value?.$el, {
      placement: "bottom-end",
      modifiers: [
        {
          name: "offset",
          options: {
            offset: [0, 4],
          },
        },
      ],
    });
  },
);
// onMounted(() => {
//   if (!props.modelValue.start || !props.modelValue.end) {
//     modelValue.value = {
//       start: DateFns.startOfDay(new Date()),
//       end: DateFns.endOfDay(new Date()),
//     }
//   }
// })
</script>
